import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  CREDIT_WALLET_PROFILE_KEY,
  CREDIT_WALLET_TRANSACTIONS_KEY,
  getCycleKey,
  getFeedProgramIdKey,
  getFeedProgramsKey,
  getPondDashboardKey,
  getPondKey,
} from 'common/constants/queryKeys'
import useAnalytics, { AnalyticsEvents } from 'common/hooks/useAnalytics'
import useCycleApi from 'common/hooks/useCycleApi'
import useFarmApi from 'common/hooks/useFarmApi'
import usePondApi from 'common/hooks/usePondApi'
import { MessageResponse } from 'common/types/api'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useSetRecoilState } from 'recoil'

import { normalizeSpeciesName } from 'utils/analytics'
import { buildUrlWithParams } from 'utils/url'

import {
  editMarkets,
  createMarkets,
  POND_REQUEST_CONFIG,
  CYCLE_REQUEST_CONFIG,
  EXPORT_REQUEST_CONFIG,
  POND_EXPORT_REQUEST_CONFIG,
  editFeedPrograms,
  createFeedPrograms,
  deleteFeedProgram,
  POND_NOTE_REQUEST_CONFIG,
} from '.'
import { selectedCycleIdState } from '../stores/atom'
import {
  useGetMarkets,
  useGetMarketsById,
  useGetPondFilterCount,
  useGetPondNames,
  useGetPonds,
  useGetSpecies,
} from './queries'
import { useGetPondsRecoilSelector, useGetPondNameById, useSpeciesName } from './selector'
import {
  CreateCycleVariables,
  CreatePondVariables,
  EditCycleVariables,
  EditPondVariables,
  ExportCyclesSummaryParams,
  ExportDailyParams,
  ExportFeaturesParams,
  PondNoteVariables,
  ProgressCb,
} from './types'

export const useMutateCreatePond = () => {
  const farmApi = useFarmApi()
  const { refetch } = useGetPonds()
  const { refetch: refetchPondsSelector } = useGetPondsRecoilSelector()
  const { refetch: refetchPondFilterCount } = useGetPondFilterCount()
  const { refetch: refetchPondNames } = useGetPondNames()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['common'])
  const { data: species } = useGetSpecies()
  const { trackEvent } = useAnalytics()

  return useMutation(
    ({ body: data }: CreatePondVariables) =>
      farmApi.request<MessageResponse>({
        ...POND_REQUEST_CONFIG.createPond,
        data,
      }),
    {
      onSuccess: (_data, variables) => {
        enqueueSnackbar(t('common:create.success'), { variant: 'success' })
        refetch()
        refetchPondFilterCount()
        refetchPondsSelector()
        refetchPondNames()
        const speciesName = species?.find((s) => s.id === variables.body.speciesId)?.name
        const normalizedSpecies = normalizeSpeciesName(speciesName)
        trackEvent(
          AnalyticsEvents.pondCreated,
          normalizedSpecies ? { species: normalizedSpecies } : undefined
        )
      },
    }
  )
}

export const useMutateRemovePond = () => {
  const farmApi = useFarmApi()
  const { refetch } = useGetPonds()
  const { refetch: refetchPondsSelector } = useGetPondsRecoilSelector()
  const { refetch: refetchPondFilterCount } = useGetPondFilterCount()
  const { refetch: refetchPondNames } = useGetPondNames()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['common'])
  const { trackEvent } = useAnalytics()

  return useMutation(
    (pondId: string) =>
      farmApi.request<MessageResponse>({
        ...POND_REQUEST_CONFIG.removePond,
        url: `${POND_REQUEST_CONFIG.removePond.url}/${pondId}`,
      }),
    {
      onSuccess: (res) => {
        const { status } = res.data
        if (status !== 200) return

        enqueueSnackbar(t('common:delete.success'), { variant: 'success' })
        refetch()
        refetchPondsSelector()
        refetchPondFilterCount()
        refetchPondNames()
        trackEvent(AnalyticsEvents.pondDeleted)
      },
    }
  )
}

export const useMutateEditPond = () => {
  const qc = useQueryClient()
  const farmApi = useFarmApi()
  const { trackEvent } = useAnalytics()

  return useMutation(
    ({ pondId, body: data }: EditPondVariables) =>
      farmApi.request<MessageResponse>({
        ...POND_REQUEST_CONFIG.editPond,
        url: `/ponds/${pondId}`,
        data,
      }),
    {
      onSuccess: (res) => {
        const { status } = res.data
        if (status !== 200) return

        qc.invalidateQueries({ queryKey: getPondKey.all() })
        trackEvent(AnalyticsEvents.pondUpdated)
      },
    }
  )
}

export const useMutateCreateCycle = () => {
  const pondApi = usePondApi()
  const { enqueueSnackbar } = useSnackbar()
  const qc = useQueryClient()
  const setCycleId = useSetRecoilState(selectedCycleIdState)
  const { t } = useTranslation(['pond'])
  const speciesName = useSpeciesName()
  const { trackEvent } = useAnalytics()

  return useMutation(
    ({ body: data }: CreateCycleVariables) =>
      pondApi.request({
        ...CYCLE_REQUEST_CONFIG.createCycle,
        data,
      }),
    {
      onSuccess: (res) => {
        const { status, data } = res
        if (status !== 200) return

        enqueueSnackbar(t('pond:new.cycle.success'), { variant: 'success' })
        setCycleId(data.data.id)
        qc.invalidateQueries({ queryKey: getCycleKey.all() })
        qc.invalidateQueries({ queryKey: getPondKey.all() })
        const normalizedSpecies = normalizeSpeciesName(speciesName)
        trackEvent(
          AnalyticsEvents.cycleStarted,
          normalizedSpecies ? { species: normalizedSpecies } : undefined
        )
      },
    }
  )
}

export const useMutateCreateCycleWithCredit = () => {
  const pondApi = usePondApi()
  const { enqueueSnackbar } = useSnackbar()
  const qc = useQueryClient()
  const setCycleId = useSetRecoilState(selectedCycleIdState)
  const { t } = useTranslation(['pond'])
  const speciesName = useSpeciesName()
  const { trackEvent } = useAnalytics()

  return useMutation(
    ({ body: data }: CreateCycleVariables) =>
      pondApi.request({
        ...CYCLE_REQUEST_CONFIG.createCycleWithCredit,
        data,
      }),
    {
      onSuccess: (res) => {
        const { status, data } = res
        if (status !== 200) return

        enqueueSnackbar(t('pond:new.cycle.success'), { variant: 'success' })
        setCycleId(data.data.id)
        qc.invalidateQueries({ queryKey: getCycleKey.all() })
        qc.invalidateQueries({ queryKey: getPondKey.all() })
        qc.invalidateQueries({ queryKey: CREDIT_WALLET_PROFILE_KEY })
        qc.invalidateQueries({ queryKey: CREDIT_WALLET_TRANSACTIONS_KEY })
        const normalizedSpecies = normalizeSpeciesName(speciesName)
        trackEvent(
          AnalyticsEvents.cycleStarted,
          normalizedSpecies ? { species: normalizedSpecies } : undefined
        )
      },
    }
  )
}

export const useMutateEditCycle = () => {
  const cycleApi = useCycleApi()
  const qc = useQueryClient()
  const { trackEvent } = useAnalytics()

  return useMutation(
    ({ body: data }: EditCycleVariables) =>
      cycleApi.request<MessageResponse>({ ...CYCLE_REQUEST_CONFIG.editCycle, data }),
    {
      onSuccess: (res) => {
        const { status } = res

        if (status !== 200) return

        qc.invalidateQueries({ queryKey: getCycleKey.all() })
        qc.invalidateQueries({ queryKey: getPondKey.all() })
        qc.invalidateQueries({ queryKey: getPondDashboardKey.all() })
        trackEvent(AnalyticsEvents.cycleUpdated)
      },
    }
  )
}

export const useMutateEndCycle = () => {
  const queryClient = useQueryClient()
  const cycleApi = useCycleApi()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['pond'])
  const speciesName = useSpeciesName()
  const { trackEvent } = useAnalytics()

  return useMutation(
    (endDate: string) =>
      cycleApi.request<MessageResponse>({
        ...CYCLE_REQUEST_CONFIG.completeCycle,
        data: { endDate },
      }),
    {
      onSuccess: async (res) => {
        const { status } = res
        if (status !== 200) return

        enqueueSnackbar(t('pond:end.cycle.success'), { variant: 'success' })
        await queryClient.invalidateQueries({ queryKey: getPondKey.all() })
        await queryClient.invalidateQueries({ queryKey: getCycleKey.all() })
        const normalizedSpecies = normalizeSpeciesName(speciesName)
        trackEvent(
          AnalyticsEvents.cycleCompleted,
          normalizedSpecies ? { species: normalizedSpecies } : undefined
        )
      },
    }
  )
}

export const useMutateRemoveCycle = () => {
  const queryClient = useQueryClient()
  const cycleApi = useCycleApi()
  const { trackEvent } = useAnalytics()

  return useMutation(() => cycleApi.request(CYCLE_REQUEST_CONFIG.removeCycle), {
    onSuccess: async (res) => {
      const { status } = res
      if (status !== 200) return
      await queryClient.invalidateQueries({ queryKey: getPondKey.all() })
      await queryClient.invalidateQueries({ queryKey: getCycleKey.all() })
      trackEvent(AnalyticsEvents.cycleDeleted)
    },
  })
}

export const useMutateEditFeedPrograms = (feedProgramId: number) => {
  const queryClient = useQueryClient()

  return useMutation(editFeedPrograms, {
    onSuccess: async (res) => {
      const { status } = res
      if (status !== 200) return

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getFeedProgramsKey() }),
        queryClient.invalidateQueries({ queryKey: getFeedProgramIdKey(feedProgramId) }),
        // Yield is computed live from the formula, so every cycle using it changes.
        queryClient.invalidateQueries({ queryKey: getPondKey.all() }),
        queryClient.invalidateQueries({ queryKey: getCycleKey.all() }),
      ])
    },
  })
}

export const useMutateCreateFeedPrograms = () => {
  const queryClient = useQueryClient()

  return useMutation(createFeedPrograms, {
    onSuccess: async (res) => {
      const { status } = res
      if (status !== 200) return

      await queryClient.invalidateQueries({ queryKey: getFeedProgramsKey() })
    },
  })
}

export const useMutateDeleteFeedProgram = () => {
  const queryClient = useQueryClient()

  return useMutation(deleteFeedProgram, {
    onSuccess: async (res, feedProgramId) => {
      const { status } = res
      if (status !== 200) return

      queryClient.removeQueries({ queryKey: getFeedProgramIdKey(feedProgramId) })
      await queryClient.invalidateQueries({ queryKey: getFeedProgramsKey() })
    },
  })
}

export const useMutateEditMarkets = (marketId: number) => {
  const { pondId } = useParams()
  const { data: pondInfo } = useGetPondNameById(pondId ?? '')

  const { refetch: refetchMarkets } = useGetMarkets(pondInfo?.species.id)
  const { refetch: refetchMarketsById } = useGetMarketsById(marketId)

  return useMutation(editMarkets, {
    onSuccess: (res) => {
      const { status } = res
      if (status !== 200) return

      refetchMarkets()
      refetchMarketsById()
    },
  })
}

export const useMutateCreateMarkets = () => {
  const { pondId } = useParams()
  const { data: pondInfo } = useGetPondNameById(pondId ?? '')

  const { refetch: refetchMarkets } = useGetMarkets(pondInfo?.species.id)

  return useMutation(createMarkets, {
    onSuccess: (res) => {
      const { status } = res
      if (status !== 200) return

      refetchMarkets()
    },
  })
}

export const useMutateExportSummary = () => {
  const cycleApi = useCycleApi()

  return useMutation((progressCb: ProgressCb) =>
    cycleApi.request({
      ...EXPORT_REQUEST_CONFIG.exportSummary,
      onDownloadProgress: progressCb,
    })
  )
}

export const useMutateExportDaily = () => {
  const cycleApi = useCycleApi()

  return useMutation(({ query, progressCb }: ExportDailyParams) =>
    cycleApi.request({
      ...EXPORT_REQUEST_CONFIG.exportDaily,
      url: buildUrlWithParams(EXPORT_REQUEST_CONFIG.exportDaily.url ?? '', { ...query }),
      onDownloadProgress: progressCb,
    })
  )
}

export const useMutateExportFeatures = () => {
  const cycleApi = useCycleApi()

  return useMutation(({ query, progressCb }: ExportFeaturesParams) =>
    cycleApi.request({
      ...EXPORT_REQUEST_CONFIG.exportFeatures,
      url: buildUrlWithParams(EXPORT_REQUEST_CONFIG.exportFeatures.url ?? '', { ...query }),
      onDownloadProgress: progressCb,
    })
  )
}

/**
 * Downloads the summaries of several cycles as a single file. Scoped to the pond
 * rather than a cycle, since the selection can span any of the pond's cycles.
 */
export const useMutateExportCyclesSummary = () => {
  const pondApi = usePondApi()

  return useMutation(({ query, progressCb }: ExportCyclesSummaryParams) =>
    pondApi.request({
      ...POND_EXPORT_REQUEST_CONFIG.exportCyclesSummary,
      url: buildUrlWithParams(POND_EXPORT_REQUEST_CONFIG.exportCyclesSummary.url ?? '', {
        ...query,
      }),
      onDownloadProgress: progressCb,
    })
  )
}

export const useMutateCreatePondNote = () => {
  const farmApi = useFarmApi()
  const { refetch } = useGetPonds()
  const { refetch: refetchPondsSelector } = useGetPondsRecoilSelector()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['common'])

  return useMutation(
    ({ pondId, body: data }: PondNoteVariables) =>
      farmApi.request<MessageResponse>({
        ...POND_NOTE_REQUEST_CONFIG.createPondNote,
        url: `/ponds/${pondId}${POND_NOTE_REQUEST_CONFIG.createPondNote.url}`,
        data,
      }),
    {
      onSuccess: () => {
        enqueueSnackbar(t('common:create.success'), { variant: 'success' })
        refetch()
        refetchPondsSelector()
      },
    }
  )
}

export const useMutateEditPinPond = () => {
  const farmApi = useFarmApi()
  const { refetch } = useGetPonds()
  const { refetch: refetchPondsSelector } = useGetPondsRecoilSelector()
  const { trackEvent } = useAnalytics()
  return useMutation(
    ({ pondId, isPin }: { pondId: string; isPin: boolean }) =>
      farmApi.request<MessageResponse>({
        ...POND_REQUEST_CONFIG.editPinPond,
        url: `/ponds/${pondId}${POND_REQUEST_CONFIG.editPinPond.url}`,
        data: { isPin },
      }),
    {
      onSuccess: (_data, variables) => {
        refetch()
        refetchPondsSelector()
        // Stringify to match mobile — Firebase Analytics params are
        // String/num only, and GA4 should see the same value type for
        // `pinned` on both platforms.
        trackEvent(AnalyticsEvents.pondPinned, { pinned: variables.isPin.toString() })
      },
    }
  )
}
